"use client";

import Image from "next/image";
import Link from "next/link";
import { Clock, MapPin } from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";
import { Typography } from "@/components/shared/typography/typography";
import type { MarketplaceListingItem } from "@/types/api/marketplace";

type MarketplaceListingListCardProps = MarketplaceListingItem & {
  className?: string;
  /** Flush list: no outer radius / shadow (parent wraps the stack) */
  isGrouped?: boolean;
};

/** Horizontal list row for mobile marketplace results (image left, details right). */
export function MarketplaceListingListCard({
  title,
  image,
  badge,
  location,
  duration,
  price,
  slug,
  href,
  className,
  isGrouped = true,
}: MarketplaceListingListCardProps) {
  const linkHref = href ?? `/experiences/${slug}`;

  return (
    <Link
      href={linkHref}
      className={cn(
        "block w-full bg-white transition-colors active:bg-gray-100",
        className
      )}>
      <article
        className={cn(
          "group flex w-full flex-row items-center gap-3 bg-white py-3 pl-4 pr-4",
          isGrouped
            ? "rounded-none border-0 border-b border-gray-200 shadow-none last:border-b"
            : "border-gray-blue-200 border shadow-sm"
        )}>
        <div className="border-gray-200 relative h-28 w-[8.5rem] shrink-0 overflow-hidden rounded-xl border-r pr-3">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover object-center"
            sizes="136px"
          />
        </div>

        <div className="flex min-h-28 min-w-0 flex-1 flex-col justify-between gap-2">
          <div className="min-w-0 space-y-1">
            {location && (
              <div className="flex items-center gap-1">
                <MapPin
                  className="text-brand-700 h-3.5 w-3.5 shrink-0"
                  strokeWidth={2.5}
                />
                <Typography
                  variant="p-sm"
                  as="span"
                  className="line-clamp-1 text-xs font-semibold text-gray-600">
                  {location}
                </Typography>
              </div>
            )}

            <Typography
              variant="p"
              as="p"
              className="line-clamp-2 text-sm text-balance font-bold text-gray-900">
              {title}
            </Typography>

            {badge && (
              <span className="inline-flex w-fit max-w-full truncate rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-extrabold text-white">
                {badge}
              </span>
            )}
          </div>

          <div className="flex items-end justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-visible">
              {duration && (
                <div className="flex items-center gap-1 leading-none">
                  <Clock
                    className="text-brand-700 h-3.5 w-3.5 shrink-0"
                    strokeWidth={3}
                  />
                  <Typography
                    variant="p-sm"
                    as="span"
                    className="text-brand-700 text-xs leading-none font-bold whitespace-nowrap">
                    {duration}
                  </Typography>
                </div>
              )}
            </div>

            <p className="shrink-0 text-right leading-tight font-bold text-gray-900 underline">
              <span className="text-sm">From </span>
              <span className="text-[1.3125rem] leading-none">
                {formatPrice(price.value, price.currency)}
              </span>
            </p>
          </div>
        </div>
      </article>
    </Link>
  );
}
